/**
 * `npx mock-host <dir>` — standalone declarative-mock-host dev server.
 *
 * Reads SDUI resources from `<dir>/**\/*.json` (recursive), wires them into the
 * {@link InMemoryMcpTransport}, and serves a Vite dev page that mounts
 * {@link DeclarativeMockHost} so plugin authors can iterate against the same
 * declarative pipeline the real host uses — without standing up the platform.
 *
 * Resource JSON shape (two accepted forms):
 *
 *   1. Tagged:   `{ "uri": "books://catalog", "tree": <SduiNode> }`
 *   2. Untagged: a bare SDUI node. The URI defaults to the file's relative
 *      path under the resources directory (with `\\` normalised to `/` and the
 *      `.json` extension stripped).
 *
 * The CLI's pure resource-loading core is exported as {@link loadResources}
 * for unit testing; the Vite-boot side effect is gated behind {@link run}.
 */

import { promises as fs } from "node:fs";
import * as path from "node:path";
import { KNOWN_PRIMITIVES } from "@ethisyscore/protocol";
import type { SduiNode } from "../host/declarative/types";
import {
    WorkerRemoteDomTransport,
    type McpHttpClient,
    type WorkerCtor,
} from "../host/worker/transport";
import { CONTRACT_B_PRIMITIVES } from "../host/worker/component-registry";

/** Map of MCP resource URI -> SDUI tree, as fed into the mock host. */
export type ResourceMap = Record<string, SduiNode>;

/**
 * Map of MCP tool name -> canned result, as fed into the mock host. Each
 * value is returned verbatim when the tool is invoked from the rendered tree
 * (Form.submitTool, Action.tool, Field.lookupTool). Plugin authors who need
 * per-call dynamic behaviour can switch from the CLI's tools.json to a
 * hand-rolled Vite app that mounts DeclarativeMockHost with a typed tools
 * object directly.
 */
export type ToolResultMap = Record<string, unknown>;

/**
 * Recursively load every `*.json` file under {@link dir} as a SDUI resource.
 *
 * - Tagged form (`{ uri, tree }`) wins when both fields are present.
 * - Untagged form uses the file's relative POSIX-normalised path (minus
 *   extension) as the resource URI.
 *
 * Throws a single aggregated error if any file fails to parse — plugin authors
 * see one failure list instead of giving up at the first bad file.
 */
export async function loadResources(dir: string): Promise<ResourceMap>
{
    const absDir = path.resolve(dir);
    const files = await walkJson(absDir);
    const out: ResourceMap = {};
    const errors: string[] = [];

    for (const file of files)
    {
        try
        {
            const raw = await fs.readFile(file, "utf8");
            const parsed = JSON.parse(raw) as unknown;
            const { uri, tree } = normaliseEntry(parsed, file, absDir);
            if (out[uri] !== undefined)
            {
                errors.push(`Duplicate resource URI '${uri}' (from ${file})`);
                continue;
            }
            out[uri] = tree;
        }
        catch (e)
        {
            errors.push(`${file}: ${(e as Error).message}`);
        }
    }

    if (errors.length > 0)
    {
        throw new Error(
            `Failed to load ${errors.length} mock-host resource(s):\n  - ${errors.join("\n  - ")}`,
        );
    }

    return out;
}

/**
 * Read `<dir>/tools.json` (if present) and return a `Record<toolName, canned>`
 * map for the mock transport. Missing file → empty map. Malformed file →
 * throws a single descriptive error.
 *
 * Expected shape: `{ "<tool-name>": <result-literal>, ... }`. The literal is
 * returned verbatim from `InMemoryMcpTransport.invokeTool(name, args)`.
 */
export async function loadTools(dir: string): Promise<ToolResultMap>
{
    const toolsPath = path.resolve(dir, "tools.json");
    try
    {
        const raw = await fs.readFile(toolsPath, "utf8");
        const parsed = JSON.parse(raw) as unknown;
        if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed))
        {
            throw new Error(`Expected an object map, got ${typeof parsed}`);
        }
        return parsed as ToolResultMap;
    }
    catch (e)
    {
        const err = e as NodeJS.ErrnoException;
        if (err.code === "ENOENT")
        {
            return {};
        }
        throw new Error(`Failed to load tools.json from ${toolsPath}: ${err.message}`);
    }
}

/**
 * Convert a parsed JSON value into a `{ uri, tree }` pair.
 * Exported for tests; not part of the public API.
 */
export function normaliseEntry(
    parsed: unknown,
    absFile: string,
    absDir: string,
): { uri: string; tree: SduiNode }
{
    if (parsed === null || typeof parsed !== "object")
    {
        throw new Error("Resource JSON must be an object");
    }
    const obj = parsed as Record<string, unknown>;
    if (typeof obj.uri === "string" && obj.tree !== undefined)
    {
        return { uri: obj.uri, tree: obj.tree as SduiNode };
    }
    // Untagged form: derive URI from the file's relative path.
    const rel = path.relative(absDir, absFile).replace(/\\/g, "/");
    const uri = rel.replace(/\.json$/i, "");
    return { uri, tree: parsed as SduiNode };
}

async function walkJson(root: string): Promise<string[]>
{
    const out: string[] = [];

    async function recurse(dir: string): Promise<void>
    {
        let entries;
        try
        {
            entries = await fs.readdir(dir, { withFileTypes: true });
        }
        catch (e)
        {
            const err = e as NodeJS.ErrnoException;
            if (err.code === "ENOENT")
            {
                throw new Error(`Resources directory not found: ${root}`);
            }
            throw e;
        }
        for (const entry of entries)
        {
            const full = path.join(dir, entry.name);
            if (entry.isDirectory())
            {
                await recurse(full);
            }
            else if (entry.isFile() && entry.name.toLowerCase().endsWith(".json"))
            {
                out.push(full);
            }
        }
    }

    await recurse(root);
    out.sort();
    return out;
}

/**
 * Render a self-contained HTML page that mounts {@link DeclarativeMockHost}
 * with a debug registry (every primitive renders to a labelled `<div>`).
 *
 * Returned as a string so the Vite middleware can serve it from memory without
 * touching the consumer's filesystem.
 */
export function renderIndexHtml(resourceUris: string[]): string
{
    const defaultUri = resourceUris[0] ?? "";
    return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>mock-host (${resourceUris.length} resources)</title>
    <style>
      body { font-family: system-ui, sans-serif; margin: 1rem; }
      header { border-bottom: 1px solid #ccc; padding-bottom: 0.5rem; margin-bottom: 1rem; }
      select { padding: 0.25rem 0.5rem; }
      [data-mock-primitive] { padding: 0.5rem; margin: 0.25rem 0; border: 1px dashed #aaa; }
      [data-mock-primitive]::before { content: attr(data-mock-primitive); font-size: 0.75rem; color: #666; display: block; }
    </style>
  </head>
  <body>
    <header>
      <strong>mock-host</strong>
      &nbsp;|&nbsp;
      <label>Resource: <select id="resource-picker">
        ${resourceUris.map(u => `<option value="${escapeHtml(u)}">${escapeHtml(u)}</option>`).join("")}
      </select></label>
    </header>
    <main id="root"></main>
    <script type="module" src="/@mock-host/entry.tsx"></script>
    <script>
      window.__MOCK_HOST_DEFAULT_URI__ = ${safeJsonForScript(defaultUri)};
      window.__MOCK_HOST_RESOURCE_URIS__ = ${safeJsonForScript(resourceUris)};
    </script>
  </body>
</html>`;
}

function escapeHtml(s: string): string
{
    return s.replace(/[&<>"']/g, c => ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        "\"": "&quot;",
        "'": "&#39;",
    }[c] as string));
}

/**
 * Safely serialise a value for embedding inside an inline `<script>` block.
 *
 * Plain `JSON.stringify` leaves `</script>` unescaped, which lets attacker-
 * controlled resource URIs break out of the script context. We replace the
 * dangerous character pairs with their unicode-escaped equivalents — still
 * valid JSON the browser eagerly parses, but inert inside HTML.
 */
function safeJsonForScript(v: unknown): string
{
    return JSON.stringify(v)
        .replace(/</g, "\\u003c")
        .replace(/>/g, "\\u003e")
        .replace(/&/g, "\\u0026");
}

/**
 * Build the virtual entry module the browser loads. Embeds the resource map
 * inline so the dev server does not have to serve JSON over a separate
 * endpoint — Vite HMR re-evaluates this module whenever resources change.
 */
export function renderEntryModule(
    resources: ResourceMap,
    tools: ToolResultMap = {},
): string
{
    const json = JSON.stringify(resources);
    const toolsJson = JSON.stringify(tools);
    // PR #16 review (Medium: mock-host primitive registry hardcoded). Derive the
    // primitive list from the protocol's KNOWN_PRIMITIVES so the mock host can
    // never accept primitives the real protocol rejects, or omit primitives the
    // real protocol added. JSON.stringify ensures the array is emitted as a JS
    // literal in the generated module — identical wire shape to the prior
    // hardcoded list, just sourced from the protocol package.
    const primitives = JSON.stringify([...KNOWN_PRIMITIVES]);
    return `
import { createRoot } from "react-dom/client";
import { createElement, useState } from "react";
import { DeclarativeMockHost } from "@ethisyscore/extension-runtime/mock-host";

const PRIMITIVES = ${primitives};

const registry = Object.fromEntries(PRIMITIVES.map(name => [
    name,
    ({ children }) => createElement("div", { "data-mock-primitive": name }, children),
]));

const resources = ${json};
const toolResults = ${toolsJson};
// Each canned tool result is wrapped in a sync handler. Authors that need
// per-call behaviour drop tools.json and hand-roll a Vite app that mounts
// DeclarativeMockHost with a typed tools object directly.
const tools = Object.fromEntries(Object.entries(toolResults).map(([name, value]) => [
    name,
    () => value,
]));
const uris = Object.keys(resources);

function App() {
    const [uri, setUri] = useState(window.__MOCK_HOST_DEFAULT_URI__ || uris[0] || "");
    // Wire the header picker to React state so authors can switch trees live.
    const picker = document.getElementById("resource-picker");
    if (picker && !picker.__wired) {
        picker.__wired = true;
        picker.value = uri;
        picker.addEventListener("change", e => setUri(e.target.value));
    }
    return createElement(DeclarativeMockHost, {
        resources,
        tools,
        registry,
        defaultResourceUri: uri,
    });
}

createRoot(document.getElementById("root")).render(createElement(App));
`;
}

// ─── Contract B (Wave 1) CLI augment ──────────────────────────────────────────
//
// Wave 0 only supported Contract A (host-rendered) — a directory of SDUI JSON
// fed into {@link DeclarativeMockHost}. Wave 1 adds Contract B (remote-runtime):
// plugin authors run `npx mock-host --render-mode remote-runtime ./worker.js`
// to spawn an in-process Worker via {@link WorkerRemoteDomTransport} against
// the same Vite dev page workflow. The semantic component registry from E2.S4
// is referenced by name — each primitive renders to a labelled placeholder so
// the receiver's substitution behaviour is observable without standing up
// gogo-ui.

/** Discriminated union of the two render modes the CLI knows about. */
export type RenderMode = "host-rendered" | "remote-runtime";

/**
 * Parsed CLI arguments. `path` is the positional argument — its meaning
 * depends on {@link renderMode}:
 *
 * - `host-rendered`: a directory of SDUI resource JSON files.
 * - `remote-runtime`: a path (or URL) to a built worker bundle.
 */
export interface ParsedArgs
{
    renderMode: RenderMode;
    path: string;
}

const RENDER_MODES: ReadonlySet<RenderMode> = new Set<RenderMode>([
    "host-rendered",
    "remote-runtime",
]);

/**
 * Parse the CLI's argv-slice. Exported so the test suite can assert flag
 * handling without booting a Vite server.
 *
 * Accepts both `--render-mode <value>` and `--render-mode=<value>`. Defaults
 * to `host-rendered` to preserve Wave 0 behaviour for existing users.
 */
export function parseArgs(args: readonly string[]): ParsedArgs
{
    let renderMode: RenderMode = "host-rendered";
    const positional: string[] = [];

    for (let i = 0; i < args.length; i++)
    {
        const arg = args[i]!;
        if (arg === "--render-mode")
        {
            const next = args[i + 1];
            if (next === undefined)
            {
                throw new Error("Usage: mock-host [--render-mode <host-rendered|remote-runtime>] <path>");
            }
            if (!RENDER_MODES.has(next as RenderMode))
            {
                throw new Error(
                    `Invalid --render-mode '${next}'. Allowed: ${[...RENDER_MODES].join(", ")}.`,
                );
            }
            renderMode = next as RenderMode;
            i++;
            continue;
        }
        if (arg.startsWith("--render-mode="))
        {
            const value = arg.slice("--render-mode=".length);
            if (!RENDER_MODES.has(value as RenderMode))
            {
                throw new Error(
                    `Invalid --render-mode '${value}'. Allowed: ${[...RENDER_MODES].join(", ")}.`,
                );
            }
            renderMode = value as RenderMode;
            continue;
        }
        positional.push(arg);
    }

    if (positional.length === 0)
    {
        throw new Error("Usage: mock-host [--render-mode <host-rendered|remote-runtime>] <path>");
    }

    return { renderMode, path: positional[0]! };
}

/**
 * Render the HTML shell for the Contract B mock host. The page hosts a
 * receiver that mounts the Remote DOM tree produced by the worker — each
 * Contract B primitive renders to a labelled placeholder.
 */
export function renderWorkerBundleIndexHtml(bundlePath: string): string
{
    const safePath = escapeHtml(bundlePath);
    return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>mock-host (remote-runtime: ${safePath})</title>
    <style>
      body { font-family: system-ui, sans-serif; margin: 1rem; }
      header { border-bottom: 1px solid #ccc; padding-bottom: 0.5rem; margin-bottom: 1rem; }
      [data-mock-primitive] { padding: 0.5rem; margin: 0.25rem 0; border: 1px dashed #aaa; }
      [data-mock-primitive]::before { content: attr(data-mock-primitive); font-size: 0.75rem; color: #666; display: block; }
    </style>
  </head>
  <body>
    <header><strong>mock-host</strong> &nbsp;|&nbsp; <em>remote-runtime: ${safePath}</em></header>
    <main id="root"></main>
    <script type="module" src="/@mock-host/worker-bundle-entry.tsx"></script>
  </body>
</html>`;
}

/**
 * Build the inline entry module the browser loads for Contract B.
 *
 * The module:
 * 1. Constructs a {@link WorkerRemoteDomTransport} pointing at the bundle.
 * 2. Registers a placeholder for every Contract B semantic primitive — the
 *    test-injectable seam from E2.S4. Production hosts would substitute
 *    gogo-ui components here.
 * 3. Wires the receiver onto the host's `<main id="root">` slot.
 *
 * In-page wiring is deliberately minimal — the goal is observability, not a
 * production receiver implementation. Authors iterate against the same
 * surface their plugins will mount into when the platform host is finished.
 */
export function renderWorkerBundleEntryModule(bundleUrl: string): string
{
    const safePrimitives = JSON.stringify([...CONTRACT_B_PRIMITIVES]);
    const safeUrl = JSON.stringify(bundleUrl);
    return `
import { createRoot } from "react-dom/client";
import { createElement } from "react";
import {
    WorkerRemoteDomTransport,
    SemanticComponentRegistry,
} from "@ethisyscore/extension-runtime/host";

const PRIMITIVES = ${safePrimitives};
const bundleUrl = ${safeUrl};

// Build the test-injectable component map — every Contract B primitive
// renders to a labelled placeholder so plugin authors can see what their
// worker is emitting before plugging in gogo-ui.
const componentMap = Object.fromEntries(PRIMITIVES.map(name => [
    name,
    ({ children }) => createElement("div", { "data-mock-primitive": name }, children),
]));
const registry = SemanticComponentRegistry.fromMap(componentMap);

// Mock-host MCP client: no platform, so every tool/resource call resolves
// to a friendly placeholder. Authors who need real MCP traffic should run
// against the full platform host.
const mcpClient = {
    fetch: async (req) => ({
        ok: true,
        data: { mockHost: true, kind: req.kind, name: req.name ?? req.uri },
    }),
};

// The capability token never crosses the worker boundary — supply a marker
// here so the host-side fetcher can still attach an Authorization header.
const transport = new WorkerRemoteDomTransport({
    bootstrapUrl: bundleUrl,
    capabilityToken: async () => "mock-host-capability-token",
    mcpClient,
});

const root = createRoot(document.getElementById("root"));
transport.onRemoteDom(() => {
    // The full Remote DOM receiver belongs to a separate concern (E3.S2's
    // WorkerSurfaceMount). For mock-host purposes the registry is the
    // observable surface — log the receiver payload for now.
    root.render(createElement("div", { "data-mock-host-primitives": PRIMITIVES.join(",") }));
});

// Expose the transport + registry on window so authors can poke at them from
// devtools (e.g., to terminate the worker between iteration cycles).
window.__MOCK_HOST_TRANSPORT__ = transport;
window.__MOCK_HOST_REGISTRY__ = registry;
`;
}

/**
 * Construction options for {@link bootContractBHost}.
 */
export interface BootContractBHostOptions
{
    /**
     * Path or canonical URL to the plugin's worker bundle. The mock host
     * serves it from the configured Vite root — production hosts always use
     * a path-pinned host-origin URL.
     */
    bundleUrl: string;
    /**
     * Injectable Worker constructor. Tests pass a fake; the production CLI
     * path uses the global `Worker`. The transport itself defaults to
     * `globalThis.Worker` when omitted — but the mock-host CLI passes through
     * the caller's choice so test seams compose cleanly.
     */
    workerCtor?: WorkerCtor;
    /**
     * Optional capability token provider. Defaults to a marker string so
     * authors don't need to wire token plumbing into local-dev.
     */
    capabilityToken?: () => Promise<string>;
    /**
     * Optional MCP HTTP client. Defaults to a stub that echoes the request
     * shape — authors who need real MCP traffic compose the platform host.
     */
    mcpClient?: McpHttpClient;
}

const defaultMockMcpClient: McpHttpClient = {
    fetch: async (req) =>
    {
        if (req.kind === "uploadDocument")
        {
            // Stub upload: echo a synthetic StoredDocument id so a standalone
            // Contract B host can drive the FE upload flow without core storage.
            return {
                ok: true,
                data: {
                    storedDocumentId: `mock-${req.meta.entityName}-${req.meta.fileName}`,
                    fileName: req.meta.fileName,
                    contentType: req.meta.contentType,
                    sizeBytes: req.buffer.byteLength,
                },
            };
        }
        const echo = req.kind === "invokeTool" ? { tool: req.name, args: req.args } : { resource: req.uri };
        return { ok: true, data: { mockHost: true, ...echo } };
    },
};

/**
 * Spawn the in-process worker that drives the Contract B mock host.
 *
 * Returns the constructed transport so callers (production CLI: the dev page;
 * tests: assertions) can observe spawn shape, dispose the worker on shutdown,
 * and inspect coalescing behaviour. Reuses
 * {@link WorkerRemoteDomTransport} so the capability-token isolation rules
 * documented in E2.S3 apply without re-implementation.
 */
export function bootContractBHost(options: BootContractBHostOptions): WorkerRemoteDomTransport
{
    return new WorkerRemoteDomTransport({
        bootstrapUrl: options.bundleUrl,
        capabilityToken: options.capabilityToken ?? (async () => "mock-host-capability-token"),
        mcpClient: options.mcpClient ?? defaultMockMcpClient,
        workerCtor: options.workerCtor,
    });
}

/**
 * Wave 1 Contract B dev server. Boots Vite, serves the virtual index page that
 * runs {@link renderWorkerBundleEntryModule} (which spawns the worker), and
 * exposes the bundle on a host-origin URL so the worker handshake can succeed.
 */
async function runContractBHost(bundlePathOrUrl: string): Promise<{ url: string; close: () => Promise<void> }>
{
    // Lazy import so the pure exports stay decoupled from Vite at type-time.
    /* eslint-disable @typescript-eslint/no-explicit-any */
    let viteMod: any;
    try
    {
        viteMod = await import("vite" as any);
    }
    catch
    {
        throw new Error(
            "vite is required to run `mock-host`. Install with: npm install --save-dev vite",
        );
    }
    /* eslint-enable @typescript-eslint/no-explicit-any */

    // Two flavours of bundle reference are accepted: an absolute URL (rare —
    // useful for testing a deployed bundle) and a filesystem path that the
    // dev server exposes as a host-origin URL.
    const isUrl = /^https?:\/\//i.test(bundlePathOrUrl);
    const absBundle = isUrl ? bundlePathOrUrl : path.resolve(bundlePathOrUrl);

    if (!isUrl)
    {
        try
        {
            await fs.access(absBundle);
        }
        catch
        {
            throw new Error(`mock-host: worker bundle not found at ${absBundle}`);
        }
    }

    // The dev page references the bundle via a stable virtual path. The Vite
    // middleware serves the file's bytes directly so the worker spawn URL is
    // host-origin (matching the production CSP path-pinning shape).
    const bundleServePath = "/@mock-host/worker-bundle.js";
    const indexHtml = renderWorkerBundleIndexHtml(isUrl ? bundlePathOrUrl : bundleServePath);
    const entryModule = renderWorkerBundleEntryModule(isUrl ? bundlePathOrUrl : bundleServePath);

    const server = await viteMod.createServer({
        root: process.cwd(),
        configFile: false,
        server: { port: 0, host: "127.0.0.1" },
        plugins: [
            {
                name: "mock-host-virtual-worker",
                resolveId(id: string)
                {
                    if (id === "/@mock-host/worker-bundle-entry.tsx")
                    {
                        return "\0mock-host:worker-bundle-entry";
                    }
                    return null;
                },
                load(id: string)
                {
                    if (id === "\0mock-host:worker-bundle-entry")
                    {
                        return entryModule;
                    }
                    return null;
                },
                /* eslint-disable @typescript-eslint/no-explicit-any */
                configureServer(devServer: any)
                {
                    devServer.middlewares.use(async (req: any, res: any, next: () => void) =>
                    {
                        if (req.url === "/" || req.url === "/index.html")
                        {
                            res.setHeader("Content-Type", "text/html");
                            res.end(indexHtml);
                            return;
                        }
                        if (!isUrl && req.url === bundleServePath)
                        {
                            try
                            {
                                const bytes = await fs.readFile(absBundle);
                                res.setHeader("Content-Type", "text/javascript; charset=utf-8");
                                res.end(bytes);
                            }
                            catch (e)
                            {
                                res.statusCode = 500;
                                res.end(`mock-host: failed to read worker bundle: ${(e as Error).message}`);
                            }
                            return;
                        }
                        next();
                    });
                },
                /* eslint-enable @typescript-eslint/no-explicit-any */
            },
        ],
    });

    await server.listen();
    const addr = server.httpServer?.address();
    let port = 5173;
    if (addr && typeof addr === "object")
    {
        port = addr.port;
    }
    const url = `http://127.0.0.1:${port}/`;
    process.stdout.write(`mock-host listening on ${url} (remote-runtime: ${absBundle})\n`);

    return { url, close: () => server.close() };
}

/**
 * Boot the Vite dev server with an in-memory entry + virtual page.
 *
 * Side-effectful: imports `vite` lazily so the pure `loadResources` export
 * stays usable from environments that lack a Vite install (e.g., the unit
 * tests in this package).
 *
 * Dispatches on `--render-mode`:
 *   - `host-rendered` (default): Wave 0 declarative-mock-host behaviour.
 *   - `remote-runtime`: Wave 1 Contract B — spawns a worker pointing at the
 *     supplied bundle path and serves the placeholder dev page.
 */
export async function run(args: string[]): Promise<{ url: string; close: () => Promise<void> }>
{
    const parsed = parseArgs(args);

    if (parsed.renderMode === "remote-runtime")
    {
        return runContractBHost(parsed.path);
    }

    const dir = parsed.path;
    const resources = await loadResources(dir);
    const tools = await loadTools(dir);
    const uris = Object.keys(resources).sort();
    if (uris.length === 0)
    {
        // Allow boot anyway — authors might want to scaffold-then-add resources
        // with HMR-watched edits.
        process.stderr.write(`mock-host: no resources found in ${dir} (HMR will pick up new files)\n`);
    }

    // Lazy import so the pure exports stay decoupled from Vite at type-time.
    // Vite is declared as an OPTIONAL peer dependency — type as `any` here so
    // building the package doesn't require Vite's type surface to be present.
    /* eslint-disable @typescript-eslint/no-explicit-any */
    let viteMod: any;
    try
    {
        viteMod = await import("vite" as any);
    }
    catch
    {
        throw new Error(
            "vite is required to run `mock-host`. Install with: npm install --save-dev vite",
        );
    }
    /* eslint-enable @typescript-eslint/no-explicit-any */

    const indexHtml = renderIndexHtml(uris);
    const entryModule = renderEntryModule(resources, tools);
    const absDir = path.resolve(dir);

    const server = await viteMod.createServer({
        root: process.cwd(),
        configFile: false,
        server: { port: 0, host: "127.0.0.1" },
        plugins: [
            {
                name: "mock-host-virtual",
                resolveId(id: string)
                {
                    if (id === "/@mock-host/entry.tsx")
                    {
                        return "\0mock-host:entry";
                    }
                    return null;
                },
                load(id: string)
                {
                    if (id === "\0mock-host:entry")
                    {
                        return entryModule;
                    }
                    return null;
                },
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                configureServer(devServer: any)
                {
                    devServer.watcher.add(absDir);
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    devServer.middlewares.use((req: any, res: any, next: () => void) =>
                    {
                        if (req.url === "/" || req.url === "/index.html")
                        {
                            res.setHeader("Content-Type", "text/html");
                            res.end(indexHtml);
                            return;
                        }
                        next();
                    });
                },
            },
        ],
    });

    await server.listen();
    const addr = server.httpServer?.address();
    let port = 5173;
    if (addr && typeof addr === "object")
    {
        port = addr.port;
    }
    const url = `http://127.0.0.1:${port}/`;
    process.stdout.write(`mock-host listening on ${url} (resources: ${uris.length})\n`);

    return { url, close: () => server.close() };
}

// No top-level bin guard here: the `mock-host` bin entry is a thin
// CJS wrapper (`bin/mock-host.cjs`, hand-written, kept out of tsup) that
// requires the compiled `cli.cjs` and calls `run(process.argv.slice(2))`.
// That keeps this module side-effect free so unit tests can import the pure
// `loadResources` / `normaliseEntry` / `renderEntryModule` exports without
// booting Vite.
